import type { MaskitoOptions } from '@maskito/core';
import { useMaskito } from '@maskito/react';
import { clsx } from 'clsx';
import type { ChangeEvent, FocusEvent, KeyboardEvent, WheelEvent } from 'react';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import { numberRegex } from '../../../utils/regex';
import { DismissFilledIcon } from '../../icon/icons/DismissFilledIcon';
import { Typography } from '../../typography/typography';
import type { NumberFieldProps } from '../field.types';
import { FieldWrapper } from '../fieldWrapper/fieldWrapper';
import { RequiredLabel } from '../requiredLabel';
import { maskOptionsFromFormat, maskValue, unmaskValue } from './mask';
import { mergeRefs } from './mergeRefs';

import styles from './numberField.module.css';

const isBlank = (value: unknown) =>
  value === undefined || value === null || value === '';

interface ParseFieldValueArgs {
  allowedDecimalPlaces?: number;
  /** The value as the input displays it, mask literals included */
  displayValue: string;
  maskOptions: MaskitoOptions | null;
  parseNumber: (value: string) => number;
}

/**
 * Reduces what the input displays down to the two representations callers are
 * handed: the raw digits, and those digits as a number.
 */
const parseFieldValue = ({
  allowedDecimalPlaces,
  displayValue,
  maskOptions,
  parseNumber,
}: ParseFieldValueArgs) => {
  const unformattedValue = unmaskValue(displayValue, maskOptions);
  const decimalAdjustedValue =
    allowedDecimalPlaces !== undefined
      ? parseNumber(unformattedValue).toFixed(allowedDecimalPlaces)
      : unformattedValue;
  const numericValue = parseNumber(decimalAdjustedValue);

  return {
    numericValue: isNaN(numericValue) ? undefined : numericValue,
    unformattedValue,
  };
};

/**
 * Number fields allow users to enter numeric values into a UI.
 *
 * Use this component *outside forms* for inputs of `type`:
 * `number`.
 *
 * **NOTE: DO NOT USE THIS COMPONENT WITHIN FORMS**
 *
 * _For a similar component for use within forms, see [`Form.NumberField`](/story/reefer-hook-form-form-numberfield--default)._
 */
export const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(
  function NumberField(
    {
      'data-testid': testId,
      allowedDecimalPlaces,
      allowLeadingZeros,
      autocomplete,
      autoFocus = false,
      borderRadius = 'sm',
      children,
      className,
      clearIcon = false,
      defaultValue,
      disabled = false,
      disableMobileInputStyling = false,
      disableOnWheel = false,
      endUnit,
      enterKeyHint,
      helperText,
      id,
      label,
      labelHidden = false,
      maskFormat,
      name,
      onBlur,
      onChange,
      onUpdate,
      placeholder,
      readOnly = false,
      required = false,
      requiredLabel = false,
      startUnit,
      step = 'any',
      value: externalValue,
      width = 'auto',
      ...props
    },
    ref
  ) {
    const parseNumber = Number.isInteger(step) ? parseInt : parseFloat;
    /**
     * A stable identity matters here beyond the usual: Maskito rebuilds its
     * input listeners whenever the options object changes.
     */
    const maskOptions = useMemo(
      () => (maskFormat ? maskOptionsFromFormat(maskFormat) : null),
      [maskFormat]
    );
    // Passing null options leaves the input unmasked, so this is safe to attach
    // whether or not a maskFormat was given.
    const maskitoRef = useMaskito({ options: maskOptions });
    const inputRef = useMemo(
      () => mergeRefs(ref, maskitoRef),
      [maskitoRef, ref]
    );

    /**
     * State holds the value as the input *displays* it: Maskito writes mask
     * literals straight to the DOM node, so anything else here would fight it
     * for the input's value and drag the caret around with it.
     */
    const [value, setValue] = useState<string | undefined>(() => {
      // Since this is a number, we need to check for undefined rather than a falsey value cause 0 should be valid
      if (defaultValue === undefined) return '';

      return maskValue(
        allowedDecimalPlaces !== undefined
          ? defaultValue.toFixed(allowedDecimalPlaces)
          : defaultValue.toString(),
        maskOptions
      );
    });
    const showDismissFilledIcon =
      clearIcon && value !== undefined && value.length > 0;

    /**
     * `onUpdate` is read through a ref instead of being declared as a
     * dependency of the sync effect below: callers commonly declare their
     * handler inline, which gives it a new identity on every render and would
     * re-run the effect — and its `onUpdate` call — on every render with it.
     */
    const onUpdateRef = useRef(onUpdate);

    useEffect(() => {
      onUpdateRef.current = onUpdate;
    }, [onUpdate]);

    // Sync the internal value whenever the external value changes out from
    // under us, then notify the caller of the value we settled on.
    useEffect(() => {
      if (externalValue === undefined || value === undefined) return;
      // An empty field is already in sync with an empty external value, and
      // comparing the two numerically never settles since parseNumber('') is
      // NaN, which is equal to nothing.
      if (isBlank(externalValue) && isBlank(value)) return;
      // Compare on the unmasked digits — a masked display never parses as the
      // number it represents.
      if (externalValue === parseNumber(unmaskValue(value, maskOptions)))
        return;

      setValue(
        maskValue(
          allowedDecimalPlaces !== undefined &&
            typeof externalValue === 'number'
            ? externalValue.toFixed(allowedDecimalPlaces)
            : externalValue.toString(),
          maskOptions
        )
      );
      onUpdateRef.current && onUpdateRef.current(externalValue);
    }, [allowedDecimalPlaces, externalValue, maskOptions, parseNumber, value]);

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
      const displayValue = event.target.value;
      const { numericValue, unformattedValue } = parseFieldValue({
        allowedDecimalPlaces,
        displayValue,
        maskOptions,
        parseNumber,
      });

      setValue(displayValue);
      onBlur?.(allowLeadingZeros ? unformattedValue : numericValue);
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const displayValue = event.target.value;
      const { numericValue, unformattedValue } = parseFieldValue({
        allowedDecimalPlaces,
        displayValue,
        maskOptions,
        parseNumber,
      });

      setValue(displayValue);
      onChange?.(allowLeadingZeros ? unformattedValue : numericValue);
    };

    const handleClear = () => {
      onBlur?.('');
      onChange?.('');
      setValue('');
    };

    const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
      const myValue = event.key;

      const isValid = numberRegex.test(myValue) || event.key === 'Enter';

      if (!isValid) {
        event.preventDefault();
      }
    };

    // blur and refocus to prevent wheel event from changing value
    const handleOnWheel = (event: WheelEvent<HTMLInputElement>) => {
      const inputElement = event.target as HTMLInputElement;
      inputElement.blur();

      setTimeout(() => {
        inputElement.focus();
      }, 0);
    };

    return (
      <FieldWrapper
        className={className}
        disabled={disabled}
        disableMobileInputStyling={disableMobileInputStyling}
        helperText={helperText}
        id={id}
        label={
          <RequiredLabel required={required && requiredLabel}>
            {label}
          </RequiredLabel>
        }
        labelHidden={labelHidden}
        labelPosition="t"
        name={name}
        width={width}
        data-testid={testId}
        isWrappingTextInput
        render={(renderProps) => (
          <div
            className={clsx(
              styles['numberField_wrapper'],
              styles[`numberField_wrapper__borderRadius_${borderRadius}`],
              {
                [styles['numberField_wrapper__readOnly']]: readOnly,
                [styles['numberField_wrapper__mobile']]:
                  !disableMobileInputStyling,
              }
            )}
          >
            {startUnit && (
              <Typography as="span" color="text-light" mr={4}>
                {startUnit}
              </Typography>
            )}
            <input
              autoComplete={autocomplete}
              autoFocus={autoFocus}
              className={clsx(styles['numberField_input'], {
                [styles['numberField_input__mobile']]:
                  !disableMobileInputStyling,
              })}
              disabled={disabled}
              enterKeyHint={enterKeyHint}
              id={name}
              name={name}
              onBlur={handleBlur}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              onWheel={disableOnWheel ? handleOnWheel : undefined}
              placeholder={placeholder}
              readOnly={readOnly}
              ref={inputRef}
              required={required}
              step={step}
              // A masked value is no longer a valid number input value, so the
              // masked field is a tel field — which also gets mobile keyboards
              // the numeric keypad.
              type={maskFormat ? 'tel' : 'number'}
              value={value}
              {...renderProps}
            />
            {showDismissFilledIcon && (
              <DismissFilledIcon
                altText="clear input"
                color="text-light"
                onClick={handleClear}
                size="md"
              />
            )}
            {endUnit && (
              <Typography
                as="span"
                color="text-light"
                ml={showDismissFilledIcon ? 16 : 4}
              >
                {endUnit}
              </Typography>
            )}
          </div>
        )}
        {...props}
      >
        {children}
      </FieldWrapper>
    );
  }
);
